import { readItemField } from './item-field-reader.js';
import { readLayerListEntryUuid } from './inventory-raw-readers.js';
import { isCastingOnlyStatusLayer, statusRuntimeLayerKeys } from '../statuses/runtime-layers.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
/** Resource guards are read directly from live providers; no numeric cluster retains their contribution. */
export const STATUS_RESOURCE_GUARD_LAYERS: ReadonlySet<string> = new Set(['mana_cannot_be_reduced', 'charges_cannot_be_reduced']);

/** Admit only fully held passive trees whose entire payload is live resource guards and lifetime metadata. */
export function runtimeOnlyStatusPassives(definition: Row, resolve: (id: string) => Row | null, entries: (row: Row, key: string) => unknown[]): boolean {
  const visiting = new Set<Row>();
  const walk = (host: Row): boolean => {
    if (visiting.has(host)) return false;
    visiting.add(host);
    for (const entry of entries(host, 'passive_ability')) {
      const row = record(entry), nested = record(row?.item_arr);
      const passive = nested ?? (record(row?.item_layer_types) ? row : resolve(upper(readLayerListEntryUuid(entry) || row?.ability_id)));
      if (!passive || !passive.item_layer_types || typeof passive.item_layer_types !== 'object' || statusRuntimeLayerKeys(passive).some(key => !STATUS_RESOURCE_GUARD_LAYERS.has(key)
        && key !== 'passive_ability' && key !== 'unenchantable' && key !== 'duration_int' && key !== 'duration_type'
        && !isCastingOnlyStatusLayer(key)) || !walk(passive)) return false;
    }
    visiting.delete(host);
    return true;
  };
  return walk(definition);
}

/** Cold status grants carry their exact status-row source. Drop only those entries, preserving native/other-status providers. */
export function removeStatusPassiveGrants(row: Row, statusIds: readonly string[]): void {
  const removed = new Set(statusIds.map(upper));
  const holders = [row, record(row.item_layer_types), record(record(record(row.computed)?.creature)?.abilities)];
  for (const holder of holders) {
    const stored = holder?.passive_ability;
    if (!holder || !stored || typeof stored !== 'object') continue;
    const list = Object.values(stored);
    const kept = list.filter(entry => {
      const source = record(entry);
      return !source || !removed.has(upper(readItemField(source, 'built_from_id') ?? readItemField(source, 'source')));
    });
    if (kept.length !== list.length) holder.passive_ability = kept;
  }
}
